import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateGuestInput,
  CreateGuestRelationInput,
  GuestCapabilities,
  GuestDuplicateGroup,
  GuestFormOptions,
  GuestImportAction,
  GuestListPage,
  GuestTableOption,
  GuestRelation,
  GuestSummary,
  UpdateGuestInput,
} from '@peles/shared';
import * as api from '../api/eventGuests';
import type {
  GuestBulkEdit,
  GuestListQuery,
  GuestRsvpInput,
  SeatGuestsInput,
} from '../api/eventGuests';

/**
 * An event's guest list through `/events/:eventId/guests`. Every key starts
 * `['guests', …]`, so anything that already refreshes the guest list (and the
 * event overview's numbers, which follow it) refreshes these too.
 */

export const eventGuestKeys = {
  all: (eventId: string) => ['guests', 'event', eventId] as const,
  list: (eventId: string, query: GuestListQuery) =>
    ['guests', 'event', eventId, 'list', query] as const,
  summary: (eventId: string) => ['guests', 'event', eventId, 'summary'] as const,
  options: (eventId: string) => ['guests', 'event', eventId, 'options'] as const,
  capabilities: (eventId: string) => ['guests', 'event', eventId, 'capabilities'] as const,
  relations: (eventId: string) => ['guests', 'event', eventId, 'relations'] as const,
  tables: (eventId: string) => ['guests', 'event', eventId, 'tables'] as const,
  duplicates: (eventId: string) => ['guests', 'event', eventId, 'duplicates'] as const,
};

export function useEventGuests(eventId: string | undefined, query: GuestListQuery = {}) {
  return useQuery<GuestListPage>({
    queryKey: eventGuestKeys.list(eventId ?? '', query),
    queryFn: () => api.listEventGuests(eventId!, query),
    enabled: !!eventId,
    // Paging and filtering keep the last page on screen while the next loads.
    placeholderData: keepPreviousData,
  });
}

export function useEventGuestSummary(eventId: string | undefined) {
  return useQuery<GuestSummary>({
    queryKey: eventGuestKeys.summary(eventId ?? ''),
    queryFn: () => api.getEventGuestSummary(eventId!),
    enabled: !!eventId,
  });
}

export function useEventGuestOptions(eventId: string | undefined) {
  return useQuery<GuestFormOptions>({
    queryKey: eventGuestKeys.options(eventId ?? ''),
    queryFn: () => api.getEventGuestOptions(eventId!),
    enabled: !!eventId,
    staleTime: 5 * 60_000,
  });
}

export function useEventGuestCapabilities(eventId: string | undefined) {
  return useQuery<GuestCapabilities>({
    queryKey: eventGuestKeys.capabilities(eventId ?? ''),
    queryFn: () => api.getEventGuestCapabilities(eventId!),
    enabled: !!eventId,
    staleTime: 5 * 60_000,
  });
}

export function useGuestRelations(eventId: string | undefined) {
  return useQuery<GuestRelation[]>({
    queryKey: eventGuestKeys.relations(eventId ?? ''),
    queryFn: () => api.listGuestRelations(eventId!),
    enabled: !!eventId,
  });
}

/** The event's tables and how full each is — the seat picker's rows. */
export function useEventGuestTables(eventId: string | undefined, enabled = true) {
  return useQuery<GuestTableOption[]>({
    queryKey: eventGuestKeys.tables(eventId ?? ''),
    queryFn: () => api.listEventGuestTables(eventId!),
    enabled: !!eventId && enabled,
  });
}

/** Guests that look like one party entered twice. */
export function useEventGuestDuplicates(eventId: string | undefined, enabled = true) {
  return useQuery<GuestDuplicateGroup[]>({
    queryKey: eventGuestKeys.duplicates(eventId ?? ''),
    queryFn: () => api.listEventGuestDuplicates(eventId!),
    enabled: !!eventId && enabled,
  });
}

/** Every write refreshes the guest list, its numbers and anything reading guests. */
function useGuestMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useCreateEventGuest(eventId: string) {
  return useGuestMutation((input: Omit<CreateGuestInput, 'eventId'>) =>
    api.createEventGuest(eventId, input),
  );
}

export function useUpdateEventGuest(eventId: string) {
  return useGuestMutation(({ guestId, updates }: { guestId: string; updates: UpdateGuestInput }) =>
    api.updateEventGuest(eventId, guestId, updates),
  );
}

export function useDeleteEventGuest(eventId: string) {
  return useGuestMutation((guestId: string) => api.deleteEventGuest(eventId, guestId));
}

export function useSetEventGuestRsvp(eventId: string) {
  return useGuestMutation(({ guestId, input }: { guestId: string; input: GuestRsvpInput }) =>
    api.setEventGuestRsvp(eventId, guestId, input),
  );
}

export function useBulkEditEventGuests(eventId: string) {
  return useGuestMutation(({ guestIds, updates }: { guestIds: string[]; updates: GuestBulkEdit }) =>
    api.bulkEditEventGuests(eventId, guestIds, updates),
  );
}

export function useBulkDeleteEventGuests(eventId: string) {
  return useGuestMutation((guestIds: string[]) => api.bulkDeleteEventGuests(eventId, guestIds));
}

export function useSetGuestRelation(eventId: string) {
  return useGuestMutation((input: CreateGuestRelationInput) =>
    api.setGuestRelation(eventId, input),
  );
}

export function useDeleteGuestRelation(eventId: string) {
  return useGuestMutation((relationId: string) => api.deleteGuestRelation(eventId, relationId));
}

export function useSeatEventGuests(eventId: string) {
  return useGuestMutation((input: SeatGuestsInput) => api.seatEventGuests(eventId, input));
}

export function useMergeEventGuests(eventId: string) {
  return useGuestMutation(({ keepId, mergeIds }: { keepId: string; mergeIds: string[] }) =>
    api.mergeEventGuests(eventId, keepId, mergeIds),
  );
}

/** Checking an import changes nothing, so it refreshes nothing. */
export function usePreviewEventGuestImport(eventId: string) {
  return useMutation({
    mutationFn: (rows: Array<Record<string, unknown>>) =>
      api.previewEventGuestImport(eventId, rows),
  });
}

export function useCommitEventGuestImport(eventId: string) {
  return useGuestMutation(
    (
      rows: Array<{ data: Record<string, unknown>; action: GuestImportAction; mergeInto?: string }>,
    ) => api.commitEventGuestImport(eventId, rows),
  );
}
